/**
 * API client. Reads/writes the JWT from localStorage and routes everything
 * through `/api` (proxied by Vite in dev to the live backend, served by
 * the same Express in prod).
 */

const TOKEN_KEY = 'telos_token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export class APIError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T = any>(
  method: string,
  path: string,
  body?: any,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
    signal: opts.signal,
    credentials: 'include',
  });

  const text = await res.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : null; } catch { json = text; }

  if (!res.ok) {
    const msg = (json && (json.error || json.message)) || `Request failed (${res.status})`;
    if (res.status === 401) {
      // Token invalid — clear it so the auth context can react
      setToken(null);
    }
    throw new APIError(res.status, msg, json);
  }
  return json as T;
}

export const api = {
  get:    <T = any>(path: string, opts?: { signal?: AbortSignal }) => request<T>('GET', path, undefined, opts),
  post:   <T = any>(path: string, body?: any, opts?: { signal?: AbortSignal }) => request<T>('POST', path, body, opts),
  patch:  <T = any>(path: string, body?: any, opts?: { signal?: AbortSignal }) => request<T>('PATCH', path, body, opts),
  put:    <T = any>(path: string, body?: any, opts?: { signal?: AbortSignal }) => request<T>('PUT', path, body, opts),
  delete: <T = any>(path: string, opts?: { signal?: AbortSignal }) => request<T>('DELETE', path, undefined, opts),
};
